import { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";

const emptyPost = { blog_id: null, title: "", body: "", cover_image_url: "", images: [], video_url: "", author_name: "", published: false };
const abs = (u) => !u ? "" : u.startsWith("http") ? u : `${BACKEND_URL}${u}`;
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return ""; } };

export default function AdminBlogManager() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // draft object
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/blog").then(({ data }) => setPosts(data)).catch(() => setPosts([]));
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...emptyPost });
  const startEdit = (p) => setEditing({
    blog_id: p.blog_id, title: p.title || "", body: p.body || "", cover_image_url: p.cover_image_url || "",
    images: p.images || [], video_url: p.video_url || "", author_name: p.author_name || "", published: !!p.published,
    slug: p.slug, updated_at: p.updated_at,
  });

  const uploadImage = async (file, target) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/admin/blog/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    if (target === "cover") setEditing((e) => ({ ...e, cover_image_url: data.url }));
    else setEditing((e) => ({ ...e, images: [...(e.images || []), data.url] }));
    toast.success("Image uploaded");
  };

  const uploadVideo = async (file) => {
    if (file.size > 500 * 1024 * 1024) return toast.error("Video exceeds 500 MB");
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/blog/upload-video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((e) => ({ ...e, video_url: data.url }));
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const save = async (publishing = null) => {
    if (!editing.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      const payload = {
        title: editing.title.trim(), body: editing.body || "",
        cover_image_url: editing.cover_image_url || null,
        images: editing.images || [], video_url: editing.video_url || null,
        author_name: editing.author_name || null,
        published: publishing == null ? editing.published : publishing,
      };
      if (editing.blog_id) {
        await api.patch(`/admin/blog/${editing.blog_id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/admin/blog", payload);
        toast.success(publishing ? "Published" : "Saved as draft");
      }
      await load();
      setEditing(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const togglePublish = async (p) => {
    try {
      await api.patch(`/admin/blog/${p.blog_id}`, { published: !p.published });
      toast.success(!p.published ? "Published" : "Unpublished");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/blog/${p.blog_id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (editing) {
    return (
      <div className="brut-border bg-white p-6 max-w-4xl space-y-5" data-testid="admin-blog-editor">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Back office · Journal</div>
            <h3 className="heading text-2xl font-bold uppercase mt-1">{editing.blog_id ? "Edit post" : "New post"}</h3>
            {editing.slug && <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#555] mt-1">slug: /blog/{editing.slug} · updated {fmt(editing.updated_at)}</div>}
          </div>
          <button onClick={() => setEditing(null)} className="brut-border bg-white px-3 py-2 mono text-[10px] uppercase tracking-[0.25em]">← Back to list</button>
        </div>

        <div>
          <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Title *</label>
          <input data-testid="blog-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" placeholder="Field notes from the porcelain kiln" />
        </div>

        <div>
          <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Body</label>
          <textarea data-testid="blog-body" rows={12} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3 leading-relaxed" placeholder="Write freely. Blank lines become paragraphs on the public page." />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Cover image</label>
            <input data-testid="blog-cover" type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0], "cover")} className="w-full mt-2 brut-border bg-white px-4 py-3 text-sm" />
            {editing.cover_image_url && <img src={abs(editing.cover_image_url)} alt="cover" className="mt-2 brut-border max-h-40 object-cover" />}
          </div>
          <div>
            <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Author name (optional)</label>
            <input data-testid="blog-author" value={editing.author_name} onChange={(e) => setEditing({ ...editing, author_name: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" placeholder="Jubasmash" />
          </div>
        </div>

        <div>
          <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Video (paste a YouTube / Vimeo URL, or upload an .mp4)</label>
          <input data-testid="blog-video-url" value={editing.video_url} onChange={(e) => setEditing({ ...editing, video_url: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" placeholder="https://youtu.be/…" />
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <label className="brut-border bg-white px-3 py-2 mono text-[10px] uppercase tracking-[0.25em] cursor-pointer hover:bg-[#1F3D2A] hover:text-white">
              {editing.video_url && editing.video_url.startsWith("/api/blog-file/") ? "Replace uploaded video" : "Or upload an .mp4"}
              <input data-testid="blog-video-file" type="file" accept="video/mp4" className="hidden" onChange={(e) => e.target.files?.[0] && uploadVideo(e.target.files[0])} />
            </label>
            {editing.video_url && <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#555] break-all">{editing.video_url.slice(0, 60)}{editing.video_url.length > 60 ? "…" : ""}</div>}
            {editing.video_url && <button onClick={() => setEditing({ ...editing, video_url: "" })} className="mono text-[10px] uppercase tracking-[0.25em] text-[#C77E3C] hover:underline">clear</button>}
          </div>
        </div>

        <div>
          <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Gallery images (optional)</label>
          <input data-testid="blog-gallery" type="file" accept="image/*" multiple onChange={async (e) => {
            const files = Array.from(e.target.files || []); e.target.value = "";
            for (const f of files) await uploadImage(f, "gallery");
          }} className="w-full mt-2 brut-border bg-white px-4 py-3 text-sm" />
          {editing.images && editing.images.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {editing.images.map((u, i) => (
                <div key={i} className="relative w-24 h-24 brut-border overflow-hidden group">
                  <img src={abs(u)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setEditing({ ...editing, images: editing.images.filter((_, j) => j !== i) })} className="absolute bottom-0 right-0 bg-[#1F3D2A] text-white mono text-[10px] px-1 opacity-0 group-hover:opacity-100">✕</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-4 border-t-2 border-[#1F3D2A]">
          <button data-testid="blog-save-draft" disabled={busy} onClick={() => save(false)} className="brut-border bg-white px-5 py-3 mono text-xs uppercase tracking-[0.25em] hover:bg-[#FDF3E9]">{editing.blog_id ? "Save changes" : "Save draft"}</button>
          <button data-testid="blog-publish" disabled={busy} onClick={() => save(true)} className="brut-border bg-[#C77E3C] text-white px-5 py-3 mono text-xs uppercase tracking-[0.25em] hover:bg-[#1F3D2A]">
            {editing.published ? "Update & keep published" : "Publish now"}
          </button>
          {editing.published && editing.blog_id && (
            <button onClick={() => save(false)} disabled={busy} className="brut-border bg-white px-3 py-3 mono text-[10px] uppercase tracking-[0.25em] hover:bg-[#C77E3C] hover:text-white">Unpublish</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="brut-border bg-white" data-testid="admin-blog-list">
      <div className="p-4 border-b-2 border-[#1F3D2A] bg-[#FDF3E9] flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Journal</div>
          <div className="heading text-xl font-bold uppercase">{posts.length} post{posts.length === 1 ? "" : "s"}</div>
        </div>
        <button data-testid="blog-new" onClick={startNew} className="brut-border bg-[#1F3D2A] text-white px-4 py-3 mono text-xs uppercase tracking-[0.25em] hover:bg-[#C77E3C]">+ New post</button>
      </div>
      {posts.length === 0 ? (
        <div className="p-12 text-center mono uppercase text-xs tracking-[0.2em] text-[#555]">No posts yet — click &ldquo;+ New post&rdquo; to write your first one.</div>
      ) : (
        <table className="w-full">
          <thead className="bg-[#1F3D2A] text-white">
            <tr className="mono text-[10px] uppercase tracking-[0.25em]">
              <th className="text-left p-3">Title</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Updated</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.blog_id} data-testid={`blog-row-${p.slug}`} className="border-t-2 border-[#1F3D2A]">
                <td className="p-3">
                  <div className="font-bold">{p.title}</div>
                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#555] mt-1">/blog/{p.slug}</div>
                </td>
                <td className="p-3"><span className={`pill-label ${p.published ? "bg-[#C77E3C]" : "bg-[#1F3D2A]"}`}>{p.published ? "PUBLISHED" : "DRAFT"}</span></td>
                <td className="p-3 mono text-[10px] uppercase tracking-[0.2em]">{fmt(p.updated_at)}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button data-testid={`blog-edit-${p.slug}`} onClick={() => startEdit(p)} className="mono text-xs uppercase tracking-[0.2em] mr-3 hover:text-[#C77E3C]">Edit</button>
                  <button onClick={() => togglePublish(p)} className="mono text-xs uppercase tracking-[0.2em] mr-3 hover:text-[#C77E3C]">{p.published ? "Unpublish" : "Publish"}</button>
                  {p.published && <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="mono text-xs uppercase tracking-[0.2em] mr-3 hover:text-[#C77E3C]">View ↗</a>}
                  <button onClick={() => remove(p)} className="mono text-xs uppercase tracking-[0.2em] hover:text-[#C77E3C]">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
